import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useCallback, useRef, useState } from 'react';

import { CVSSEEvent } from 'types/edgeFunctions';

interface UseCVStreamOptions {
  onSection?: (sectionType: string, data: unknown) => void;
  onMeta?: (field: string, value: unknown) => void;
  onProgress?: (stage: string, message: string) => void;
  onDone?: (payload?: Record<string, unknown>) => void;
  onError?: (message: string) => void;
}

type SSECallbacks = {
  onSection: React.MutableRefObject<UseCVStreamOptions['onSection']>;
  onMeta: React.MutableRefObject<UseCVStreamOptions['onMeta']>;
  onProgress: React.MutableRefObject<UseCVStreamOptions['onProgress']>;
  onDone: React.MutableRefObject<UseCVStreamOptions['onDone']>;
  onError: React.MutableRefObject<UseCVStreamOptions['onError']>;
};

const processSSELines = (lines: string[], callbacks: SSECallbacks) => {
  let currentEvent = '';

  for (const line of lines) {
    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7).trim();
    } else if (line.startsWith('data: ') && currentEvent) {
      try {
        const data = JSON.parse(line.slice(6)) as CVSSEEvent;

        switch (data.type) {
          case 'cv_section':
            callbacks.onSection.current?.(data.sectionType, data.data);
            break;
          case 'cv_meta':
            callbacks.onMeta.current?.(data.field, data.value);
            break;
          case 'cv_progress':
            callbacks.onProgress.current?.(data.stage, data.message);
            break;
          case 'cv_done': {
            const donePayload: Record<string, unknown> = {};
            if (data.cvId) donePayload.cvId = data.cvId;
            if (data.truncated) donePayload.truncated = true;
            callbacks.onDone.current?.(Object.keys(donePayload).length > 0 ? donePayload : undefined);
            break;
          }
          case 'cv_error':
            callbacks.onError.current?.(data.message);
            break;
        }
      } catch {
        // Skip malformed JSON
      }
      currentEvent = '';
    }
  }
};

export { processSSELines };

export const useCVStream = (options: UseCVStreamOptions) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const supabase = useSupabaseTypedClient();

  // Store callbacks in refs to avoid stale closures and prevent
  // sendRequest from being recreated on every render
  const onSectionRef = useRef(options.onSection);
  const onMetaRef = useRef(options.onMeta);
  const onProgressRef = useRef(options.onProgress);
  const onDoneRef = useRef(options.onDone);
  const onErrorRef = useRef(options.onError);
  onSectionRef.current = options.onSection;
  onMetaRef.current = options.onMeta;
  onProgressRef.current = options.onProgress;
  onDoneRef.current = options.onDone;
  onErrorRef.current = options.onError;

  const sendRequest = useCallback(
    async (functionName: string, body: Record<string, unknown>) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        onErrorRef.current?.('Not authenticated');
        return;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const url = `${supabaseUrl}/functions/v1/${functionName}`;

      abortControllerRef.current = new AbortController();
      setIsStreaming(true);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = 'Something went wrong';

          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            // Use default error message
          }

          onErrorRef.current?.(errorMessage);
          setIsStreaming(false);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          onErrorRef.current?.('No response stream');
          setIsStreaming(false);
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';
        const callbacks: SSECallbacks = {
          onSection: onSectionRef,
          onMeta: onMetaRef,
          onProgress: onProgressRef,
          onDone: onDoneRef,
          onError: onErrorRef,
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          processSSELines(lines, callbacks);
        }

        // Flush remaining buffer after stream ends
        buffer += decoder.decode(new Uint8Array(), { stream: false });
        if (buffer.trim()) {
          const remainingLines = buffer.split('\n');
          processSSELines(remainingLines, callbacks);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Stream cancelled by user — silent
        } else {
          onErrorRef.current?.(err instanceof Error ? err.message : 'Stream connection failed');
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supabase]
  );

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
  }, []);

  return { sendRequest, cancelStream, isStreaming };
};
